import { useState, useReducer, useRef, useEffect } from "react"
import { select } from 'd3-selection'
import { axisBottom, axisLeft } from 'd3-axis'
import { scaleLinear } from 'd3-scale'
import { zoom } from 'd3-zoom'
import Select from "../widgets/Select.jsx";
import { downloadFile, decoratedlist, horizontalList, HeaderWithModal } from "../utils.jsx"


// Standard genetic code (DNA codons -> one-letter amino acids; '*' = stop).
const CODON_TABLE = {
    TTT: "F", TTC: "F", TTA: "L", TTG: "L", CTT: "L", CTC: "L", CTA: "L", CTG: "L",
    ATT: "I", ATC: "I", ATA: "I", ATG: "M", GTT: "V", GTC: "V", GTA: "V", GTG: "V",
    TCT: "S", TCC: "S", TCA: "S", TCG: "S", CCT: "P", CCC: "P", CCA: "P", CCG: "P",
    ACT: "T", ACC: "T", ACA: "T", ACG: "T", GCT: "A", GCC: "A", GCA: "A", GCG: "A",
    TAT: "Y", TAC: "Y", TAA: "*", TAG: "*", CAT: "H", CAC: "H", CAA: "Q", CAG: "Q",
    AAT: "N", AAC: "N", AAA: "K", AAG: "K", GAT: "D", GAC: "D", GAA: "E", GAG: "E",
    TGT: "C", TGC: "C", TGA: "*", TGG: "W", CGT: "R", CGC: "R", CGA: "R", CGG: "R",
    AGT: "S", AGC: "S", AGA: "R", AGG: "R", GGT: "G", GGC: "G", GGA: "G", GGG: "G"
};

// Normalize an RNA/DNA sequence for translation (uppercase, U->T). Character
// count is preserved, so indices map 1:1 back to the original sequence.
const normalizeSeq = s => (typeof s === "string" ? s.toUpperCase().replace(/U/g, "T") : null);

// Translate a nucleotide sequence starting at `frame` (0/1/2).
const translateFrame = (seq, frame) => {
    let aa = "";
    for (let i = frame; i + 3 <= seq.length; i += 3) {
        aa += CODON_TABLE[seq.slice(i, i + 3)] || "X";
    }
    return aa;
};

// Locate the exact protein within a translated frame, also allowing the
// protein without its leading residue (non-AUG starts often differ there).
const findProtein = (peptide, protein) => {
    if (!peptide || !protein) return null;
    let idx = peptide.indexOf(protein);
    if (idx >= 0) return {aaOffset: idx};
    if (protein.length > 1) {
        idx = peptide.indexOf(protein.slice(1));
        if (idx >= 0) return {aaOffset: idx - 1};
    }
    return null;
};

// Find the coding subsequence for `protein` in the forward transcript by
// translating all three frames. Returns [start, end) nt coordinates of the
// codons that encode the protein (stop codon excluded), or null.
const findCodingRange = (rnaSeq, proteinSeq) => {
    const seq = normalizeSeq(rnaSeq);
    const protein = normalizeSeq(proteinSeq);
    if (!seq || !protein) return null;
    for (let f = 0; f < 3; f++) {
        const hit = findProtein(translateFrame(seq, f), protein);
        if (hit) {
            const start = f + hit.aaOffset * 3;
            return [start, start + protein.length * 3];
        }
    }
    return null;
};


const GeneModelDrawer = ({transcriptList, highlightedProtein}) => {
    
    const [expanded, setExpanded] = useState(new Set());
    const [rnaModalData, setRnaModalData] = useState(null);
    const [copied, setCopied] = useState(false);
    
    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    
    const margin = {top: 50, right: 30, bottom: 20, left: 180}
    const width = 1000 - margin.left - margin.right
    const heightPerTranscript = 40
    const proteinRowHeight = 30;
    const proteinPadding = 20;
    
    const totalHeight = transcriptList.reduce((height, transcript, i) => {
        let transcriptHeight = heightPerTranscript;
        if (expanded.has(i)) {
            const numProteins = transcript.trxp.length;
            transcriptHeight += proteinPadding + (numProteins * proteinRowHeight);
        }
        return height + transcriptHeight;
    }, 0);
    const svgRef = useRef();
    function reverse(segments, maxVal) {
        return (
            segments.map(pair => pair.map(num => Math.abs(maxVal - num))).map(inner => inner.slice().sort((a, b) => a - b)).sort((a, b) => a[0] - b[0])
        )
    }
    useEffect(() => {
        if (svgRef.current && transcriptList.length > 0) {
            const svg = select(svgRef.current)
            svg.selectAll("*").remove()
            svg.attr("height", totalHeight + margin.top + margin.bottom)

            let globalMax = 0;
            let globalMin = Infinity;
            transcriptList.forEach(transcript => {
                const { trxp } = transcript;
                const segments = JSON.parse(trxp.transcript_segments || trxp[0]?.transcript_segments || '[]');
                if (segments.length > 0) {
                    const minValue = Math.min(...segments.flat());
                    const maxValue = Math.max(...segments.flat());
                    if (minValue < globalMin) globalMin = minValue;
                    if (maxValue > globalMax) globalMax = maxValue;
                }
            });

            const xScale = scaleLinear().domain([0, globalMax - globalMin]).range([margin.left, width + margin.left]);

            let currentYOffset = 0;

            transcriptList.forEach((transcript, i) => {
                const { trxp, protDetails, accession } = transcript
                const rna_seq = trxp[0]?.rna_seq;
                const segments = JSON.parse(trxp[0]?.transcript_segments || '[]');
                if (segments.length === 0) return;

                let exons = segments.map(pair => pair.map(num => num - globalMin));
                
                if (protDetails && protDetails.length > 0 && protDetails[0].strand === -1) {
                    exons = reverse(exons, globalMax - globalMin);
                }
                
                const exonGaps = exons.slice(0, -1).map((exon, idx) => [exon[1], exons[idx + 1][0]]);
                
                const yOffset = currentYOffset + margin.top;

                // Add accession text, make clickable
                const accessionText = svg.append("text")
                    .attr("x", 10)
                    .attr("y", yOffset + heightPerTranscript / 2)
                    .attr("text-anchor", "start")
                    .attr("dominant-baseline", "middle")
                    .style("font-size", "12px")
                    .style("cursor", "pointer")
                    .style("fill", "blue")
                    .text(accession)
                     .on("click", () => {
                         setExpanded(prev => {
                             const newSet = new Set(prev);
                             if (newSet.has(i)) {
                                 newSet.delete(i);
                             } else {
                                 newSet.add(i);
                             }
                             return newSet;
                         });
                     });

                 // Draw exons
                svg.selectAll(`.exons-${i}`)
                    .data(exons)
                    .join("path")
                    .attr("class", `exons-${i}`)
                    .attr("d", (exon) => `M${xScale(exon[0])},${yOffset + 10}L${xScale(exon[1])},${yOffset + 10}`)
                    .attr("fill", "none")
                    .attr("stroke", "black")
                    .attr("stroke-width", 4);

                // Draw exon gaps
                svg.selectAll(`.exonGaps-${i}`)
                    .data(exonGaps)
                    .join("path")
                    .attr("class", `exonGaps-${i}`)
                    .attr("d", (gap) => {
                        const gapHalf = gap[0] + (Math.abs(gap[1] - gap[0]) / 2);
                        return `M${xScale(gap[0])},${yOffset + 10}L${xScale(gapHalf)},${yOffset + 5}L${xScale(gap[1])},${yOffset + 10}`;
                    })
                    .attr("fill", "none")
                    .attr("stroke", "black")
                    .attr("stroke-width", 0.5);

                currentYOffset += heightPerTranscript;

                if (expanded.has(i)) {
                    const sortedTrxp = [...trxp].sort((a, b) => JSON.parse(a.prot_segments)[0][0] - JSON.parse(b.prot_segments)[0][0]);
                    if (protDetails[0].strand === -1) {
                        sortedTrxp.sort((a, b) => JSON.parse(b.prot_segments)[0][1] - JSON.parse(a.prot_segments)[0][1]);
                    }

                    sortedTrxp.forEach((protein, j) => {
                        const protSegments = JSON.parse(protein.prot_segments).map(pair => pair.map(num => num - globalMin));
                        let segs = protSegments;
                        if (protDetails[0].strand === -1) {
                            segs = reverse(segs, globalMax - globalMin);
                        }

                        let segColor = "#7C83D2";
                        let segBorderColor = "#B1B3E5";
                        if (protein.prot_type === "cryptic") {
                            segColor = "#C77182";
                            segBorderColor = "#E6A0AD";
                        } else if (protein.prot_type === "isoform") {
                            segColor = "#E4C780";
                            segBorderColor = "#F0DBA8";
                        }

                        const isHighlighted = protein.prot_acc === highlightedProtein;

                        if (isHighlighted) {
                            const glowLayers = [
                                { width: 12, opacity: 0.3 },
                                { width: 10, opacity: 0.5 },
                                { width: 8, opacity: 0.7 }
                            ];
                            glowLayers.forEach(layer => {
                                svg.selectAll(`.prot-segs-${i}-${j}-glow-${layer.width}`)
                                    .data(segs)
                                    .join("path")
                                    .attr("class", `prot-segs-${i}-${j}-glow-${layer.width}`)
                                    .attr("d", (seg) => `M${xScale(seg[0])},${yOffset + 20 + (30 * j)}L${xScale(seg[1])},${yOffset + 20 + (30 * j)}`)
                                    .attr("fill", "none")
                                    .attr("stroke", segBorderColor)
                                    .attr("stroke-width", layer.width)
                                    .attr("stroke-opacity", layer.opacity);
                            });
                        }

                        svg.selectAll(`.prot-segs-${i}-${j}`)
                            .data(segs)
                            .join("path")
                            .attr("class", `prot-segs-${i}-${j}`)
                            .attr("d", (seg) => `M${xScale(seg[0])},${yOffset + 20 + (30 * j)}L${xScale(seg[1])},${yOffset + 20 + (30 * j)}`)
                            .attr("fill", "none")
                            .attr("stroke", segColor)
                            .attr("stroke-width", 6);

                        svg.selectAll(`.prot-label-${i}-${j}`)
                            .data([protein])
                            .join("text")
                            .attr("class", `prot-label-${i}-${j}`)
                            .attr("x", xScale(segs[0][0]))
                            .attr("y", yOffset + 35 + (30 * j))
                            .style("font-size", "12px")
                            .style("font-weight", isHighlighted ? "bold" : "normal")
                            .style("fill", isHighlighted ? "black" : "blue")
                            .style("cursor", isHighlighted ? "default" : "pointer")
                            .on("click", isHighlighted ? null : () => window.open(`https://test.openprot.org/proteins/HS/${protein.protein_seq_id}`, '_blank'))
                            .text(d => d.prot_acc)
                    });

                    currentYOffset += proteinPadding + (trxp.length * proteinRowHeight);

                    // Add RNA sequence button under accession text when expanded
                    if (rna_seq) {
                        svg.append("text")
                            .attr("x", 10)
                            .attr("y", yOffset + heightPerTranscript + 20)
                            .attr("text-anchor", "start")
                            .attr("dominant-baseline", "middle")
                            .style("font-size", "10px")
                            .style("cursor", "pointer")
                            .style("fill", "#28a745")
                            .style("font-weight", "bold")
                            .text("[RNA]")
                            .on("click", (event) => {
                                event.stopPropagation();
                                setRnaModalData({rna_seq, proteinSeq: protDetails?.[0]?.sequence});
                            });
                    }
                }
            });
        }
    }, [transcriptList, expanded, highlightedProtein]);

    return (
        <>
            <svg ref={svgRef} width={width + margin.left + margin.right} height={totalHeight + margin.top + margin.bottom}></svg>
            {rnaModalData && (
                <div className="fixed z-50 p-4 flex items-center justify-center" style={{top: 0, left: 0, right: 0, bottom: 0}}
                     onClick={() => { setRnaModalData(null); setCopied(false); }}>
                    <div className="relative w-full max-w-3xl flex flex-col" style={{maxHeight: '96vh'}}
                         onClick={e => e.stopPropagation()}>
                        <div className="relative bg-white rounded-lg shadow flex flex-col" style={{maxHeight: '96vh'}}>
                            <div className="flex items-center justify-between p-5 border-b rounded-t shrink-0">
                                <h3 className="text-xl font-medium text-gray-900">
                                    RNA Sequence
                                </h3>
                                <button onClick={() => { setRnaModalData(null); setCopied(false); }}
                                        className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center">
                                    <svg aria-hidden="true" className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path fill-rule="evenodd"
                                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                              clip-rule="evenodd"></path>
                                    </svg>
                                    <span className="sr-only">Close modal</span>
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto" style={{height: '70vh', maxHeight: '70vh'}}>
                                <p className="text-sm font-mono break-words leading-relaxed">
                                    {(() => {
                                        const {rna_seq, proteinSeq} = rnaModalData;
                                        const range = findCodingRange(rna_seq, proteinSeq);
                                        if (!range) return rna_seq;
                                        const [start, end] = range;
                                        return <>
                                            {rna_seq.slice(0, start)}
                                            <b>{rna_seq.slice(start, end)}</b>
                                            {rna_seq.slice(end)}
                                        </>;
                                    })()}
                                </p>
                            </div>
                            <div className="flex items-center p-6 space-x-2 border-t border-gray-200 rounded-b shrink-0">
                                <button
                                    onClick={() => copyToClipboard(rnaModalData.rna_seq)}
                                    className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10"
                                >
                                    {copied ? 'Copied!' : 'Copy'}
                                </button>
                                <button
                                    onClick={() => { setRnaModalData(null); setCopied(false); }}
                                    className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default GeneModelDrawer
